import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy } from "lucide-react";
import { buildBookingPageQrSvg, svgToDataUrl } from "../../features/trial-requests/utils/bookingPage";

/** How long Copy reads "Copied". */
export const COPIED_MS = 2000;

interface ShareLinkBlockProps {
  id: string;
  label: string;
  url: string;
  hint?: string;
}

/** One address to hand on, stacked like the app's sheet: heading, hint, QR code, address, Copy. */
const ShareLinkBlock = ({ id, label, url, hint }: ShareLinkBlockProps) => {
  const { t } = useTranslation();
  const qr = useMemo(() => svgToDataUrl(buildBookingPageQrSvg(url)), [url]);
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async (): Promise<void> => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  return (
    <div className="space-y-3 py-5 text-center" data-testid={`share-${id}`}>
      <h3 className="text-base font-semibold text-grey-20 dark:text-white">{label}</h3>
      {hint && <p className="text-sm text-grey-40 dark:text-grey-80">{hint}</p>}
      {/* QR codes need a light background to scan — the white chip stays in dark mode too. */}
      <img
        src={qr}
        alt={t("shareLinks.qrAlt")}
        data-testid={`share-${id}-qr`}
        className="mx-auto h-40 w-40 rounded-lg border border-grey-90 bg-white p-1 dark:border-grey-50"
      />
      <p
        data-testid={`share-${id}-url`}
        className="break-all rounded-lg bg-grey-95 px-3 py-2 text-sm text-grey-20 dark:bg-primary-20 dark:text-white"
      >
        {url}
      </p>
      <button type="button" className="btn btn-secondary" onClick={() => void copy()}>
        <Copy size={16} />
        {copied ? t("shareLinks.copied") : t("shareLinks.copy")}
      </button>
    </div>
  );
};

export default ShareLinkBlock;
